import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '@/services/api';
import { useAuth } from './AuthContext';

interface ColdStorageLocation {
  id: string;
  name: string;
  location: string;
  capacity: number;
  currentStock: number;
  temperature: number;
  isActive: boolean;
}

interface ShopLocation {
  id: string;
  name: string;
  location: string;
  address: string;
  zone: string;
  phone: string;
  isActive: boolean;
}

interface LocationContextType {
  coldStorages: ColdStorageLocation[];
  shops: ShopLocation[];
  isMultiCF: boolean;
  isMultiShop: boolean;
  selectedCFIds: string[];
  selectedShopIds: string[];
  setSelectedCFIds: (ids: string[]) => void;
  setSelectedShopIds: (ids: string[]) => void;
  allCFIds: string[];
  allShopIds: string[];
  loading: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const STORAGE_KEY_CF = 'location_filter_cf';
const STORAGE_KEY_SHOP = 'location_filter_shop';

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, user } = useAuth();
  const [coldStorages, setColdStorages] = useState<ColdStorageLocation[]>([]);
  const [shops, setShops] = useState<ShopLocation[]>([]);
  const [selectedCFIds, setSelectedCFIdsState] = useState<string[]>([]);
  const [selectedShopIds, setSelectedShopIdsState] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const allCFIds = coldStorages.map(cs => cs.id);
  const allShopIds = shops.map(s => s.id);
  const isMultiCF = coldStorages.length > 1;
  const isMultiShop = shops.length > 1;

  const setSelectedCFIds = useCallback((ids: string[]) => {
    setSelectedCFIdsState(ids);
    localStorage.setItem(STORAGE_KEY_CF, JSON.stringify(ids));
  }, []);

  const setSelectedShopIds = useCallback((ids: string[]) => {
    setSelectedShopIdsState(ids);
    localStorage.setItem(STORAGE_KEY_SHOP, JSON.stringify(ids));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setColdStorages([]);
      setShops([]);
      setLoading(false);
      return;
    }

    const fetchLocations = async () => {
      try {
        const response = await apiService.request<{
          success: boolean;
          data: { coldStorages: ColdStorageLocation[]; shops: ShopLocation[] };
        }>('/user/locations');

        const cs = response.data?.coldStorages || [];
        const sh = response.data?.shops || [];

        setColdStorages(cs);
        setShops(sh);

        // Restore saved filters or default to all
        const savedCF = localStorage.getItem(STORAGE_KEY_CF);
        if (savedCF) {
          try {
            const parsed = JSON.parse(savedCF);
            const validIds = parsed.filter((id: string) => cs.some(c => c.id === id));
            setSelectedCFIdsState(validIds.length > 0 ? validIds : cs.map(c => c.id));
          } catch {
            setSelectedCFIdsState(cs.map(c => c.id));
          }
        } else {
          setSelectedCFIdsState(cs.map(c => c.id));
        }

        const savedShop = localStorage.getItem(STORAGE_KEY_SHOP);
        if (savedShop) {
          try {
            const parsed = JSON.parse(savedShop);
            const validIds = parsed.filter((id: string) => sh.some(s => s.id === id));
            setSelectedShopIdsState(validIds.length > 0 ? validIds : sh.map(s => s.id));
          } catch {
            setSelectedShopIdsState(sh.map(s => s.id));
          }
        } else {
          setSelectedShopIdsState(sh.map(s => s.id));
        }
      } catch {
        // Silently fail — user may not have any managed locations
        setColdStorages([]);
        setShops([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [isAuthenticated, user?.id]);

  return (
    <LocationContext.Provider
      value={{
        coldStorages,
        shops,
        isMultiCF,
        isMultiShop,
        selectedCFIds,
        selectedShopIds,
        setSelectedCFIds,
        setSelectedShopIds,
        allCFIds,
        allShopIds,
        loading,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocations = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocations must be used within a LocationProvider');
  }
  return context;
};
